import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, CheckCircle2, ChevronLeft, ShieldCheck, Loader2 } from "lucide-react";
import apiService from "../services/api";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError(t('auth.passwords_dont_match'));
      return;
    }
    if (code.length !== 6) {
      setError(t('auth.enter_6_digit'));
      return;
    }
    
    setIsSubmitting(true);
    setError("");
    
    try {
      const response = await apiService.post('/auth/reset-password', { token: code, password });
      if (response.success) {
        setIsSuccess(true);
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch (err) {
      setError(err.message || t('auth.invalid_code_or_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-1/4 right-1/4 w-full max-w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/4 w-full max-w-[400px] h-[400px] bg-emerald-600/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <button 
          onClick={() => navigate("/forgot-password")}
          className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors mb-8 group"
        >
          <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-xs font-black uppercase tracking-widest">{t('common.back')}</span>
        </button>

        <div className="bg-white/[0.02] border border-white/5 p-10 rounded-[32px] backdrop-blur-xl shadow-2xl">
          {!isSuccess ? (
            <>
              <div className="w-16 h-16 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl flex items-center justify-center text-indigo-400 mb-8">
                <ShieldCheck size={32} />
              </div>
              
              <h1 className="text-3xl font-black font-outfit tracking-tight text-white mb-3">
                {t('auth.new_password')}
              </h1>
              <p className="text-slate-400 font-medium mb-8 leading-relaxed">
                {t('auth.reset_instructions')}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                    Code de vérification
                  </label>
                  <input
                    type="text"
                    required
                    maxLength="6"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full bg-slate-950/50 border border-white/5 rounded-2xl py-4 px-4 text-white text-center text-2xl tracking-[0.3em] font-bold outline-none focus:border-indigo-500/50 focus:bg-slate-950 transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                    Nouveau mot de passe
                  </label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                      <Lock size={18} />
                    </div>
                    <input
                      type="password"
                      required
                      placeholder="••••••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-slate-950/50 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white outline-none focus:border-indigo-500/50 focus:bg-slate-950 transition-all font-medium"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                    {t('auth.confirm_password')}
                  </label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                      <Lock size={18} />
                    </div>
                    <input
                      type="password"
                      required
                      placeholder="••••••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-slate-950/50 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white outline-none focus:border-indigo-500/50 focus:bg-slate-950 transition-all font-medium"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs font-bold">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || code.length !== 6 || !password || !confirmPassword}
                  className="w-full py-4 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 rounded-2xl font-black text-xs uppercase tracking-[0.2em] text-white transition-all shadow-xl shadow-indigo-500/20 flex items-center justify-center gap-3 active:scale-95"
                >
                  {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : t('auth.reset_action')}
                  {!isSubmitting && <ArrowRight size={18} />}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-400 mb-8 mx-auto">
                <CheckCircle2 size={32} />
              </div>
              
              <h1 className="text-3xl font-black font-outfit tracking-tight text-white mb-3">
                {t('common.success')}
              </h1>
              <p className="text-slate-400 font-medium mb-10 leading-relaxed">
                {t('auth.reset_success_redirect')}
              </p>

              <button
                onClick={() => navigate("/login")}
                className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl font-black text-xs uppercase tracking-[0.2em] text-white transition-all"
              >
                {t('auth.go_to_login')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
